import { useState } from 'react'
import { toast } from 'sonner'

import { EditDriverModal } from '@/components/DriversTable'
import { EditEquipmentModal } from '@/components/EquipmentTable'
import { trailerConfig } from '@/components/TrailersTable'
import { truckConfig } from '@/components/TrucksTable'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Textarea } from '@/components/ui/textarea'
import { type Driver, formatDriverDates } from '@/models/driver'
import type { Equipment } from '@/models/equipment'
import { type PolicyChangeInput, PolicyChangeKind } from '@/models/policyChange'
import type { UnitOption } from './PickUnitForm'

// The fields the roster's forms edit, as they return them.
export const VEHICLE_FIELDS: (keyof Equipment)[] = [
  'vehicleNumber',
  'vin',
  'make',
  'model',
  'year',
  'licensePlate',
  'licenseState',
  'vehicleClass',
  'weightClass',
  'price',
]
export const DRIVER_FIELDS: (keyof Driver)[] = [
  'firstName',
  'lastName',
  'email',
  'mobile',
  'dob',
  'licenseState',
  'licenseNumber',
  'dateOfHire',
  'cdlDate',
  'violationsCount',
  'accidentsCount',
  'suspensionsCount',
]

export function changedFields<T extends object>(before: T, after: T, names: (keyof T)[]): Partial<T> {
  return Object.fromEntries(
    names.filter((name) => after[name] !== before[name]).map((name) => [name, after[name]])
  ) as Partial<T>
}

interface EditUnitFormProps {
  kind: PolicyChangeKind.CHANGE_VEHICLE | PolicyChangeKind.CHANGE_DRIVER
  options: UnitOption[]
  trucks: Equipment[]
  trailers: Equipment[]
  drivers: Driver[]
  /** Rejects with the API's error, which the form shows on its fields. */
  onAdd: (change: PolicyChangeInput) => Promise<void>
}

/** Pick a truck, trailer or driver and edit it on the roster's own form; the change keeps what was edited. */
export function EditUnitForm({ kind, options, trucks, trailers, drivers, onAdd }: EditUnitFormProps) {
  const vehicle = kind === PolicyChangeKind.CHANGE_VEHICLE
  const [picked, setPicked] = useState<string>()
  const [editing, setEditing] = useState(false)
  const [description, setDescription] = useState('')
  const option = options.find((candidate) => candidate.value === picked)
  const id = picked?.split(':')[1]
  const vehicles = option?.type === 'trailer' ? trailers : trucks
  const driver = option?.type === 'driver' ? drivers.find((candidate) => candidate.id === id) : undefined
  const vehicleUnit =
    option && option.type !== 'driver' ? vehicles.find((candidate) => candidate.id === id) : undefined

  function open() {
    setDescription('')
    setEditing(true)
  }

  async function send(edits: Record<string, unknown>) {
    if (!option || !id) return
    if (!Object.keys(edits).length && !description.trim()) {
      toast.info('Change at least one detail.')
      return
    }
    await onAdd({ kind, [`${option.type}Id`]: id, unit: edits, description })
    setEditing(false)
    setPicked(undefined)
  }

  const note = (
    <Field>
      <FieldLabel htmlFor={`${kind}-description`} optional>
        Anything else we should know
      </FieldLabel>
      <Textarea
        id={`${kind}-description`}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
    </Field>
  )

  return (
    <div className="flex flex-col gap-4">
      <Field>
        <FieldLabel htmlFor={`${kind}-unit`}>{vehicle ? 'Which vehicle?' : 'Which driver?'}</FieldLabel>
        <SearchableSelect
          id={`${kind}-unit`}
          options={options}
          value={picked}
          onChange={setPicked}
          placeholder={vehicle ? 'Pick a vehicle on your policies' : 'Pick a driver on your policies'}
          searchPlaceholder={vehicle ? 'Search unit number, VIN or plate' : 'Search name or license'}
          emptyMessage="None match that"
        />
      </Field>
      <div>
        <Button type="button" variant="outline" disabled={!driver && !vehicleUnit} onClick={open}>
          Edit details
        </Button>
      </div>
      {editing && driver && (
        <EditDriverModal
          driver={driver}
          existingDrivers={drivers.filter((other) => other.id !== id)}
          onClose={() => setEditing(false)}
          onSubmit={(edited) => send({ ...formatDriverDates(changedFields(driver, edited, DRIVER_FIELDS)) })}
          enforceCarrierRequired
          hideOwnerToggle
        >
          {note}
        </EditDriverModal>
      )}
      {editing && vehicleUnit && (
        <EditEquipmentModal
          item={vehicleUnit}
          isNew={false}
          existingItems={vehicles.filter((other) => other.id !== id)}
          onClose={() => setEditing(false)}
          onSubmit={(edited) => send({ ...changedFields(vehicleUnit, edited, VEHICLE_FIELDS) })}
          config={option?.type === 'trailer' ? trailerConfig : truckConfig}
          enforceCarrierRequired
        >
          {note}
        </EditEquipmentModal>
      )}
    </div>
  )
}
